import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Mic2,
  FileAudio,
  TrendingUp,
  BarChart3,
  Wallet,
  CreditCard,
  Settings,
  HelpCircle,
  X,
  Shield,
  Users,
  UsersRound,
  Megaphone,
  ShoppingBag,
  Building2,
  CalendarDays,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useShows } from "@/contexts/ShowsContext";

interface PodcasterSidebarProps {
  onNavigate?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
}

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  comingSoon?: boolean;
  beta?: boolean;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    ]
  },
  {
    label: "Content",
    items: [
      { to: "/shows", icon: Mic2, label: "Shows" },
      { to: "/episodes", icon: FileAudio, label: "Episodes" },
      { to: "/delivery", icon: TrendingUp, label: "Delivery Status" },
    ]
  },
  {
    label: "Growth",
    items: [
      { to: "/analytics-v2", icon: BarChart3, label: "Analytics" },
      { to: "/promotions", icon: Megaphone, label: "Promotions" },
    ]
  },
  {
    label: "Studios",
    items: [
      { to: "/book-studio", icon: Building2, label: "Book A Studio" },
      { to: "/my-bookings", icon: CalendarDays, label: "My Bookings" },
    ]
  },
  {
    label: "Financial",
    items: [
      { to: "/wallet", icon: Wallet, label: "Wallet", beta: true },
      { to: "/revenue", icon: TrendingUp, label: "Revenue", beta: true },
      { to: "/revenue-split", icon: Users, label: "Revenue Splits", beta: true },
    ]
  },
  {
    label: "Account",
    items: [
      { to: "/team", icon: UsersRound, label: "Team" },
      { to: "/billing", icon: CreditCard, label: "Billing" },
      { to: "/settings", icon: Settings, label: "Settings" },
      { to: "/help", icon: HelpCircle, label: "Help & Support" },
    ]
  },
];

function GroupLabel({ label }: { label: string }) {
  return (
    <div className="px-3 py-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-sidebar-muted/70">
        {label}
      </span>
    </div>
  );
}

export function PodcasterSidebar({ onNavigate, showCloseButton, onClose }: PodcasterSidebarProps) {
  const location = useLocation();
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const isNetworkAdmin = hasRole('network_admin');
  const showAdminPanel = isAdmin || isNetworkAdmin;
  const adminPanelTo = isAdmin ? '/admin' : '/admin/podcasts';
  const { shows } = useShows();
  const hasShows = shows.length > 0;

  const isActive = (to: string) => {
    return location.pathname === to || 
      (to !== "/dashboard" && location.pathname.startsWith(to));
  };

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      {/* Close Button for drawer mode */}
      {showCloseButton && onClose && (
        <div className="flex h-14 items-center px-4 border-b border-sidebar-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close menu</span>
          </Button>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        {navGroups.map((group, groupIndex) => (
          <div 
            key={groupIndex} 
            className={cn(
              groupIndex > 0 && "border-t border-sidebar-border pt-3 mt-3"
            )}
          >
            {group.label && <GroupLabel label={group.label} />}
            <div className="space-y-1">
              {group.items.filter((item) => {
                if (item.to === "/episodes" && !hasShows) return false;
                return true;
              }).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive(item.to)
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-muted hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                  {item.comingSoon && (
                    <span className="ml-auto rounded-full bg-accent/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none text-accent">
                      Soon
                    </span>
                  )}
                  {item.beta && (
                    <span className="ml-auto rounded-full bg-accent px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none text-white">
                      Beta
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Admin Panel Link - Visible to platform admins and network admins */}
      {showAdminPanel && (
        <div className="p-4 border-t border-sidebar-border">
          <NavLink
            to={adminPanelTo}
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              location.pathname.startsWith('/admin')
                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                : "text-sidebar-muted hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
            )}
          >
            <Shield className="h-4 w-4" />
            Admin Panel
          </NavLink>
        </div>
      )}
    </div>
  );
}
